import * as React from "react"
import { createPortal } from "react-dom"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { useTopLevelPortal } from "@homerunner-next/widget-core/runtime"
import { cn } from "../../lib/utils"
import { usePortalContainer } from "../../common/portal-context"

const Dialog = DialogPrimitive.Root

const DialogTrigger = DialogPrimitive.Trigger

const DialogPortal = DialogPrimitive.Portal

const overlayStyle: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.6)',
  zIndex: 99998,
}

const contentWrapperStyle: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  zIndex: 99999,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 20,
}

const closeButtonStyle: React.CSSProperties = {
  position: 'absolute',
  top: 12,
  right: 12,
  width: 28,
  height: 28,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  borderRadius: 4,
  opacity: 0.6,
  padding: 0,
}

// Visually-hidden (screen-reader-only) style — inline so it works even when
// Tailwind's `sr-only` utility isn't present in the elevated dialog portal.
const srOnlyStyle: React.CSSProperties = {
  position: 'absolute',
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: 'hidden',
  clip: 'rect(0, 0, 0, 0)',
  whiteSpace: 'nowrap',
  border: 0,
}

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, style, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn('hr-dialog-overlay', className)}
    style={{ ...overlayStyle, ...style }}
    {...props}
  />
))
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

// ---------------------------------------------------------------------------
// DialogContent – the main content wrapper with optional DOM elevation
// ---------------------------------------------------------------------------
interface DialogContentProps extends Omit<React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>, 'title'> {
  /** When true, portal the dialog to a top-level shadow root outside the widget,
   *  escaping host-page stacking contexts (sticky sidebars, navbars, etc.). */
  elevateDom?: boolean;
  /** Dynamic CSS string injected into the elevated shadow root.
   *  Typically the same CSS passed to <Stylesheet> in the widget content. */
  cssString?: string;
  /** Accessible title for the dialog. Rendered as a visually-hidden Radix
   *  DialogTitle so screen readers announce it and Radix's a11y requirement
   *  is satisfied. Pass this for dialogs that have no visible <DialogTitle>;
   *  omit it when the children already render one. */
  title?: React.ReactNode;
  /** Extra class on the backdrop overlay. Lets a caller restyle (or fully
   *  suppress) the scrim without reaching around DialogContent — the SBP
   *  mobile sheet is opaque and full-bleed, and the toolkit reference renders
   *  it with NO backdrop at all (#870), so a dark scrim would only flash
   *  during the slide-up. */
  overlayClassName?: string;
  /** Inline overrides merged onto the `.hr-dialog-wrapper` positioning div.
   *  The wrapper owns WHERE the dialog sits (its base style is a fullscreen
   *  centering flexbox), so callers that need a different placement — the SBP
   *  desktop popovers anchor to the search bar's rect instead of centering —
   *  override it here. Additive: no caller passing nothing is affected. */
  wrapperStyle?: React.CSSProperties;
  /** Suppress the corner X button. The toolkit's SBP popovers are headerless
   *  with no close affordance beyond outside-click/Escape (both still work —
   *  the transparent overlay keeps capturing clicks), so the anchored desktop
   *  popovers pass this. The sr-only DialogTitle stays either way. Additive:
   *  no caller passing nothing is affected. */
  hideClose?: boolean;
}

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  DialogContentProps
>(({ className, children, style, onWheel, onTouchMove, elevateDom, cssString, title, overlayClassName, wrapperStyle, hideClose, ...props }, ref) => {
  const portalContainer = usePortalContainer();

  // Consolidated on widget-core's elevated portal (it owns the singleton
  // host, stylesheet cloning, and the #691 theme-marker mirror).
  const topLevelPortal = useTopLevelPortal({
    cssString,
    enabled: !!elevateDom,
  });

  // SSR-safe gate for the light-DOM a11y mirror below. `typeof document`
  // differs between the server render (undefined → nothing) and the client
  // HYDRATION pass (defined → portal) — a structural mismatch that made React
  // throw away the entire SSR tree and re-render every hydrated widget from
  // scratch. Post-mount state is false during hydration on both sides (the
  // Radix Portal pattern), so the mirror appears only after mount.
  const [mounted, setMounted] = React.useState(false);
  React.useEffect(() => setMounted(true), []);

  const effectiveContainer = topLevelPortal ?? portalContainer;

  return (
    <>
      <DialogPortal container={effectiveContainer}>
        <DialogOverlay className={overlayClassName} />
        <div className="hr-dialog-wrapper" style={{ ...contentWrapperStyle, ...wrapperStyle }}>
          <DialogPrimitive.Content
            ref={ref}
            className={cn('hr-dialog-content', className)}
            style={style}
            // We never render a Radix <DialogDescription>; declaring this
            // undefined opts out of Radix's description requirement (otherwise
            // it warns "Missing Description or aria-describedby={undefined}").
            aria-describedby={undefined}
            onWheel={(e) => { e.stopPropagation(); onWheel?.(e) }}
            onTouchMove={(e) => { e.stopPropagation(); onTouchMove?.(e) }}
            {...props}
          >
            {title != null && (
              <DialogPrimitive.Title style={srOnlyStyle}>{title}</DialogPrimitive.Title>
            )}
            {children}
            {!hideClose && (
              // The class is a styling hook only (r7 dashboard#936: the SBP
              // mobile sheet restyles this into the module's circled close);
              // the inline style stays the base look for every other dialog.
              <DialogPrimitive.Close className="hr-dialog-close" style={closeButtonStyle}>
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M18 6 6 18" />
                  <path d="m6 6 12 12" />
                </svg>
                <span className="sr-only">Close</span>
              </DialogPrimitive.Close>
            )}
          </DialogPrimitive.Content>
        </div>
      </DialogPortal>
      {/* Light-DOM mirror of the Radix DialogTitle. The real, functional title
          lives inside the elevated shadow-root portal (correct aria-labelledby
          within that tree), but Radix's accessibility check runs
          `document.getElementById(titleId)`, which can't pierce shadow DOM and
          would log a false-positive error on every elevated dialog. Rendering a
          hidden <DialogTitle> (same titleId, via Dialog context) into the light
          DOM makes that lookup succeed. Rendered as a sibling of DialogPortal
          (not a child) so Radix's Portal doesn't swallow the nested portal. */}
      {elevateDom && mounted && createPortal(
        <DialogPrimitive.Title style={srOnlyStyle} aria-hidden>{title ?? ''}</DialogPrimitive.Title>,
        document.body,
      )}
    </>
  );
})
DialogContent.displayName = DialogPrimitive.Content.displayName

const DialogHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col space-y-1.5 text-center sm:text-left",
      className
    )}
    {...props}
  />
)
DialogHeader.displayName = "DialogHeader"

const DialogFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2",
      className
    )}
    {...props}
  />
)
DialogFooter.displayName = "DialogFooter"

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn(
      "text-lg font-semibold leading-none tracking-tight",
      className
    )}
    {...props}
  />
))
DialogTitle.displayName = DialogPrimitive.Title.displayName

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-sm text-[color:var(--muted-foreground)]", className)}
    {...props}
  />
))
DialogDescription.displayName = DialogPrimitive.Description.displayName

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
}
